import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import useCurrency from "../../../../../hooks/useCurrency";
import { isCookieAuth } from "../../../../../lib/authProvider";
import {
  fetchAdsHealthAlerts,
  fetchAdsHealthAlertsAcrossStores,
  storeAnalyticsSelector,
} from "../../../../../redux/slices/store_analytics";
import { useStores } from "../shared/Storepicker";

const FAMILY_LABEL = {
  BUDGET: "Budget",
  CPC: "CPC",
  EFFICIENCY: "Efficiency",
  AMAZON_DIAGNOSTIC: "Amazon",
};

const FAMILY_COLOR = {
  BUDGET: "#3b82f6",
  CPC: "#8b5cf6",
  EFFICIENCY: "#10b981",
  AMAZON_DIAGNOSTIC: "#6b7280",
};

const fmtMoney = (v, symbol) =>
  `${symbol}${(v ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// The all-stores queue is served from an hourly precomputed snapshot, not a
// live query, so we surface when it was generated. The backend emits
// generated_at as naive UTC (utcnow().isoformat(), no timezone); mark it UTC
// before parsing so the browser localizes it instead of reading it as local.
// Returns null for missing/unparseable input (e.g. the cold-cache "warming"
// payload, where generated_at is null).
const fmtAsOf = (iso) => {
  if (!iso) return null;
  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso);
  const d = new Date(hasTz ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const SummaryCard = ({ label, value, color }) => (
  <Paper elevation={1} sx={{ p: 2, minWidth: 160, flex: 1 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600}>
      {label}
    </Typography>
    <Typography
      variant="h5"
      fontWeight={700}
      sx={{ color: color || "text.primary", mt: 0.5 }}
    >
      {value}
    </Typography>
  </Paper>
);

const FamilyChip = ({ family }) => (
  <Chip
    size="small"
    label={FAMILY_LABEL[family] || family}
    sx={{
      bgcolor: (FAMILY_COLOR[family] || "#6b7280") + "18",
      color: FAMILY_COLOR[family] || "#6b7280",
      fontWeight: 600,
    }}
  />
);

export default function AlertsQueue({ store, marketplace, onSelect }) {
  const dispatch = useDispatch();
  const { marketplaceCurrencySymbol } = useCurrency();
  const {
    adsHealthAlerts,
    adsHealthLoading,
    adsHealthErrorMessage,
    adsHealthProgress,
  } = useSelector(storeAnalyticsSelector);
  const stores = useStores();

  const isAllStores = store === "all";

  // The legacy path reads the precomputed /ads-health/all snapshot. Cookie
  // callers are authorized per store, so that view is fetched store by store.
  const fanOutStores = useMemo(
    () =>
      isAllStores && isCookieAuth()
        ? stores
            .filter((option) => option.value !== "all")
            .map((option) => option.value.replace(/_client$/, ""))
        : null,
    [isAllStores, stores]
  );
  const fanningOut = Boolean(
    adsHealthProgress && adsHealthProgress.done < adsHealthProgress.total
  );
  const failedStores = adsHealthProgress?.failedStores ?? [];

  // An alert names the backend store code, which never carries the `_client`
  // suffix the picker appends on the JWT path.
  const storeLabel = (s) => {
    if (!s) return "—";
    const match = stores.find(
      (option) => option.value.replace(/_client$/, "") === s
    );
    return match?.label || s.charAt(0).toUpperCase() + s.slice(1);
  };

  useEffect(() => {
    if (!store) return;
    if (fanOutStores) {
      dispatch(
        fetchAdsHealthAlertsAcrossStores({
          stores: fanOutStores,
          marketplace: marketplace || "us",
        })
      );
      return;
    }
    dispatch(fetchAdsHealthAlerts({ store, marketplace: marketplace || "us" }));
  }, [store, marketplace, dispatch, fanOutStores]);

  if (!store) {
    return (
      <Paper sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
        Pick a store to see alerts.
      </Paper>
    );
  }
  if (adsHealthLoading) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <CircularProgress />
        {isAllStores && (
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 2, color: "text.secondary" }}
          >
            {fanOutStores
              ? `Loading alerts for ${fanOutStores.length} stores…`
              : "Loading the latest alerts snapshot…"}
          </Typography>
        )}
      </Box>
    );
  }
  if (adsHealthErrorMessage) {
    return (
      <Paper sx={{ p: 3, color: "error.main" }}>
        Failed to load alerts: {adsHealthErrorMessage}
      </Paper>
    );
  }

  const summary = adsHealthAlerts?.summary || {};
  const alerts = adsHealthAlerts?.alerts || [];
  const asOf = fmtAsOf(adsHealthAlerts?.generated_at);
  const atRiskHeader = `${marketplaceCurrencySymbol} at risk`;

  const columns = [
    ...(isAllStores ? ["Store"] : []),
    "Campaign",
    "Family",
    "Issue",
    "Spend (7d)",
    atRiskHeader,
    "Recommended action",
  ];

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <SummaryCard
          label="At-risk spend"
          value={fmtMoney(summary.dollars_at_risk, marketplaceCurrencySymbol)}
        />
        <SummaryCard label="Alerts" value={alerts.length} color="#b91c1c" />
      </Stack>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 2, mt: -1 }}
      >
        {asOf
          ? `Data as of ${asOf}${
              isAllStores && !fanOutStores ? " · auto-refreshes hourly" : ""
            }`
          : "Preparing the latest data…"}
        {fanningOut &&
          ` · Loaded ${adsHealthProgress.done} of ${adsHealthProgress.total} stores…`}
      </Typography>

      {failedStores.length > 0 && (
        <Typography
          variant="caption"
          color="error.main"
          sx={{ display: "block", mb: 2, mt: -1 }}
        >
          Could not load alerts for {failedStores.map(storeLabel).join(", ")}.
        </Typography>
      )}

      {alerts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
          {fanningOut
            ? "No alerts in the stores loaded so far."
            : "No campaigns need attention right now."}
        </Paper>
      ) : (
        <Paper elevation={1}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((h) => (
                    <TableCell
                      key={h}
                      align={
                        h === atRiskHeader || h === "Spend (7d)"
                          ? "right"
                          : "left"
                      }
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        color: "#475569",
                        letterSpacing: "0.06em",
                        backgroundColor: "#f8fafc",
                        borderBottom: "2px solid #e2e8f0",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {alerts.map((a, i) => (
                  <TableRow
                    key={`${a.store || ""}-${a.campaignId}-${a.ruleId}-${i}`}
                    hover
                    sx={{
                      cursor: onSelect ? "pointer" : "default",
                      "&:hover": { backgroundColor: "#f1f5f9" },
                    }}
                    onClick={() => onSelect && onSelect(a)}
                  >
                    {isAllStores && (
                      <TableCell>
                        <Chip
                          size="small"
                          label={storeLabel(a.store)}
                          sx={{
                            bgcolor: "#e0e7ff",
                            color: "#3730a3",
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: "#1e293b" }}
                      >
                        {a.campaignName}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.7rem" }}
                      >
                        {a.adProduct?.replace("SPONSORED_", "S ") || ""}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <FamilyChip family={a.family} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{a.message}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontFamily: "monospace",
                        }}
                      >
                        {fmtMoney(a.spend7d, marketplaceCurrencySymbol)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{
                          color:
                            a.dollarsAtRisk > 0 ? "#b91c1c" : "text.secondary",
                          fontFamily: "monospace",
                        }}
                      >
                        {fmtMoney(a.dollarsAtRisk, marketplaceCurrencySymbol)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.8rem" }}
                      >
                        {a.recommendedAction}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
